import { FabricImage, FabricObject } from 'fabric';
import { MnemonicFabricCanvas } from './fabric-canvas.types';
import { initializeMnemonicFabricRuntime } from './fabric-serialization.config';
import { extractMnemonicCanvasMeta, stripMnemonicCanvasMeta } from './fabric-canvas.parser';
import { MnemonicCanvasLoadOptions } from './fabric-canvas.types';
import { isSerializedImageType, resolveMnemonicImageId } from './fabric-object.enricher';
import { prepareMnemonicCanvasForLoad, resolveMnemonicImageLoadSrc } from './fabric-object.normalizer';
import { fabricObjectLoadReviverFn, normalizeFabricCanvasAfterLoad } from './fabric-load.reviver';

export async function deserializeMnemonicCanvas(
    canvas: MnemonicFabricCanvas,
    raw: unknown,
    options: MnemonicCanvasLoadOptions = {},
): Promise<void> {
    initializeMnemonicFabricRuntime();

    const prepared = prepareMnemonicCanvasForLoad(raw, options);
    if (!prepared) {
        return;
    }

    const meta = extractMnemonicCanvasMeta(prepared);
    const fabricJson = stripMnemonicCanvasMeta(prepared);

    await canvas.loadFromJSON(fabricJson, fabricObjectLoadReviverFn as never);
    await restoreMissingMnemonicImages(canvas, prepared, options);
    normalizeFabricCanvasAfterLoad(canvas);

    if (meta.viewPort) {
        canvas.setViewportTransform(meta.viewPort as never);
    }

    if (meta.canvasZoom) {
        canvas.setZoom(meta.canvasZoom);
    }

    canvas.requestRenderAll();
}

async function restoreMissingMnemonicImages(
    canvas: MnemonicFabricCanvas,
    prepared: Record<string, unknown>,
    options: MnemonicCanvasLoadOptions,
): Promise<void> {
    const preparedObjects = Array.isArray(prepared.objects) ? (prepared.objects as Record<string, unknown>[]) : [];

    for (let index = 0; index < preparedObjects.length; index += 1) {
        const object = preparedObjects[index];

        if (!isSerializedImageType(object)) {
            continue;
        }

        const imageId = resolveMnemonicImageId(object);
        const isLoaded = canvas.getObjects().some((loadedObject: any) => {
            const loadedImageId = resolveMnemonicImageId(loadedObject);
            return imageId ? loadedImageId === imageId : loadedObject.type === 'image';
        });

        if (isLoaded) {
            continue;
        }

        const src = resolveMnemonicImageLoadSrc(object, options);
        if (!src) {
            continue;
        }

        try {
            const { type, filters, resizeFilter, crossOrigin, ...imageOptions } = object;
            const image = await FabricImage.fromURL(src, {}, { ...imageOptions, src } as any);
            canvas.insertAt(index, image);
        } catch (error) {
            console.warn('[mnemonic] Failed to restore missing image at index', index, error);
        }
    }
}
